import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import {
	DeleteTheContactPictureForThePhoneCallNumberCommand,
	MergeThePhoneCallPhoneBookEntriesCommand,
	ProcessPhoneCallCommand,
	SetTheContactPictureForThePhoneCallNumberCommand,
	UpdateThePhoneCallPhoneBookEntryName,
	UpdateThePhoneCallPhoneBookNameNumber,
} from 'src/stores/phoneCall/commands';
import {
	GetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampQuery,
	GetTheOutgoingCallConfigurationForTheUserQuery,
	GetThePhoneBookQueryQuery,
	GetThePhoneticSimilarPhoneBookEntriesQuery,
	PhoneCallQuery,
} from 'src/stores/phoneCall/queries';

import { CommandProcessIdHandling } from 'src/models/command';
import {
	NextOutgoingCallIdForACalledNumberAfterAGivenTimestamp,
	NextOutgoingCallIdForACalledNumberAfterAGivenTimestampRunType,
	OutgoingCallConfigurationForTheUser,
	OutgoingCallConfigurationForTheUserRunType,
	PhoneBookEntries,
	PhoneBookEntriesRunType,
	PhoneCall,
	PhoneCallRunType,
	PhoneticSimilarPhoneBookEntries,
	PhoneticSimilarPhoneBookEntriesRunType,
} from 'src/models/phoneCall';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';

import {
	GetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampQueryCallback,
	GetTheOutgoingCallConfigurationForTheUserQueryCallback,
	GetThePhoneBookQueryQueryCallback,
	GetThePhoneticSimilarPhoneBookEntriesQueryCallback,
	PhoneCallCallback,
} from './callbacks';
import {
	DeleteTheContactPictureForPhoneCallNumberPayload,
	FetchPhoneCallPayload,
	FetchTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampPayload,
	FetchTheOutgoingCallConfigurationForTheUserPayload,
	FetchThePhoneBookEntriesPayload,
	FetchThePhoneticSimilarPhoneBookEntriesPayload,
	MergeThePhoneBookEntriesPayload,
	PhoneCallStoreState,
	ResetPayload,
	SaveThePhoneCallPayload,
	SetOutgoingCallWithHangupFunctionalityCallIdPayload,
	SetTheContactPictureForPhoneCallNumberPayload,
	SetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampPayload,
	SetTheOutgoingCallConfigurationForTheUserPayload,
	SetThePhoneBookEntriesPayload,
	SetThePhoneCallStatePayload,
	SetThePhoneticSimilarPhoneBookEntriesPayload,
	UnsubscribeFetchPhoneCallPayload,
	UpdateTheNewCallerNamePayload,
	UpdateThePhoneCallCallBackDesiredStatePayload,
	UpdateThePhoneCallCallDirectionPayload,
	UpdateThePhoneCallCallerNamePayload,
	UpdateThePhoneCallPhoneBookEntryNamePayload,
	UpdateThePhoneCallPhoneBookEntryNumberPayload,
	UpdateThePhoneCallReasonPayload,
	UpdateThePhoneCallReceivedByPayload,
	UpdateThePhoneCallReceiverPayload,
	UpdateThePhoneCallSummaryPayload,
	UpdateTheUpdatedCallerNamePayload,
	UpdateTheUpdatedCallerNumberPayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): PhoneCallStoreState => ({
	initialLoadedPhoneCalls: new Map<string, PhoneCall>(),
	nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp: null,
	outgoingCallConfigurationForTheUser: null,
	outgoingCallWithHangupFunctionalityCallId: null,
	phoneBookEntries: null,
	phoneCalls: new Map<string, PhoneCall>(),
	phoneticSimilarPhoneBookEntries: null,
});

const resolveStateCommandPayload = (
	payload: SetThePhoneCallStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

export const usePhoneCallStore = defineStore('phoneCall', {
	actions: {
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.phoneCalls, this.initialLoadedPhoneCalls],
				payload.aggregateIds,
				payload.status
			);
		},
		async deleteTheContactPictureForPhoneCallNumber(
			payload: DeleteTheContactPictureForPhoneCallNumberPayload
		): Promise<void> {
			const response = await payload.commandBus?.sendCommand(
				new DeleteTheContactPictureForThePhoneCallNumberCommand(payload.aggregateId, payload.phoneCallNumber)
			);

			if (response === null || response === undefined) {
				throw new Error('Contact picture delete command failed');
			}
		},
		fetchPhoneCall(payload: FetchPhoneCallPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<PhoneCall>({
					callback: new PhoneCallCallback((phoneCall) => this.setPhoneCall(phoneCall)),
					createTime: new Date(),
					query: new PhoneCallQuery(payload.aggregateId),
					responseType: 'json',
					runType: PhoneCallRunType,
				});
			}

			this.setPhoneCall(null);
			return Promise.resolve();
		},
		fetchTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(
			payload: FetchTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampPayload
		): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<NextOutgoingCallIdForACalledNumberAfterAGivenTimestamp>({
					callback: new GetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampQueryCallback(
						(nextOutgoingCallIdPayload) =>
							this.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(nextOutgoingCallIdPayload)
					),
					createTime: new Date(),
					query: new GetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampQuery(
						payload.number,
						payload.timestamp
					),
					responseType: 'json',
					runType: NextOutgoingCallIdForACalledNumberAfterAGivenTimestampRunType,
				});
			}

			this.setTheOutgoingCallConfigurationForTheUser({
				outgoingCallConfigurationForTheUser: null,
			});

			return Promise.resolve();
		},
		async fetchTheOutgoingCallConfigurationForTheUser(
			payload: FetchTheOutgoingCallConfigurationForTheUserPayload
		): Promise<void> {
			this.setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp({
				nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp: null,
			});

			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<OutgoingCallConfigurationForTheUser>({
					callback: new GetTheOutgoingCallConfigurationForTheUserQueryCallback(
						(outgoingCallConfigurationPayload) =>
							this.setTheOutgoingCallConfigurationForTheUser(outgoingCallConfigurationPayload)
					),
					createTime: new Date(),
					query: new GetTheOutgoingCallConfigurationForTheUserQuery(),
					responseType: 'json',
					runType: OutgoingCallConfigurationForTheUserRunType,
				});
			}
		},
		async fetchThePhoneBookEntries(payload: FetchThePhoneBookEntriesPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<PhoneBookEntries>({
					callback: new GetThePhoneBookQueryQueryCallback((phoneBookEntriesPayload) =>
						this.setThePhoneBookEntries(phoneBookEntriesPayload)
					),
					createTime: new Date(),
					query: new GetThePhoneBookQueryQuery(
						payload.searchString,
						payload.page,
						payload.limit,
						payload.sort
					),
					responseType: 'json',
					runType: PhoneBookEntriesRunType,
				});
				return;
			}

			this.setThePhoneBookEntries({
				phoneBookEntries: [] as unknown as PhoneBookEntries,
			});
		},
		async fetchThePhoneticSimilarPhoneBookEntries(
			payload: FetchThePhoneticSimilarPhoneBookEntriesPayload
		): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<PhoneticSimilarPhoneBookEntries>({
					callback: new GetThePhoneticSimilarPhoneBookEntriesQueryCallback(
						(phoneticSimilarPhoneBookEntriesPayload) =>
							this.setThePhoneticSimilarPhoneBookEntries(phoneticSimilarPhoneBookEntriesPayload)
					),
					createTime: new Date(),
					query: new GetThePhoneticSimilarPhoneBookEntriesQuery(),
					responseType: 'json',
					runType: PhoneticSimilarPhoneBookEntriesRunType,
				});
				return;
			}

			this.setThePhoneticSimilarPhoneBookEntries({
				phoneticSimilarPhoneBookEntries: null,
			});
		},
		initialLoadedPhoneCall(aggregateId: string): PhoneCall | undefined {
			return this.initialLoadedPhoneCalls.get(normalizeAggregateId(aggregateId));
		},
		markPhoneCallAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const phoneCallFromCollection = this.phoneCalls.get(normalizedAggregateId);
			if (phoneCallFromCollection !== undefined) {
				this.initialLoadedPhoneCalls.set(normalizedAggregateId, cloneDeep<PhoneCall>(phoneCallFromCollection));
			}
		},
		async mergeThePhoneBookEntries(payload: MergeThePhoneBookEntriesPayload): Promise<void> {
			await payload.commandBus?.sendCommand(
				new MergeThePhoneCallPhoneBookEntriesCommand(
					payload.primaryPhoneBookEntryId,
					payload.replacedPhoneBookEntryIds,
					payload.ignoredPhoneBookEntryIds
				)
			);
		},
		phoneCall(aggregateId: string): PhoneCall | undefined {
			return this.phoneCalls.get(aggregateId);
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);

			this.phoneCalls.delete(normalizedAggregateId);
			this.initialLoadedPhoneCalls.delete(normalizedAggregateId);
		},
		async saveThePhoneCall(payload: SaveThePhoneCallPayload): Promise<boolean> {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));
			if (phoneCallFromCollection === undefined) {
				return false;
			}

			const response = await payload.commandBus?.sendCommand(
				new ProcessPhoneCallCommand(phoneCallFromCollection)
			);

			return response !== null && response !== undefined;
		},
		setOutgoingCallWithHangupFunctionalityCallId(
			payload: SetOutgoingCallWithHangupFunctionalityCallIdPayload
		): void {
			this.outgoingCallWithHangupFunctionalityCallId = payload.callId;
		},
		setPhoneCall(payload: PhoneCall | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const phoneCallFromCollection = this.phoneCalls.get(normalizedAggregateId);
			const initialLoadedPhoneCallFromCollection = this.initialLoadedPhoneCalls.get(normalizedAggregateId);

			if (
				phoneCallFromCollection !== undefined &&
				!AggregateComparator.isEqual(phoneCallFromCollection, payload)
			) {
				return;
			}

			if (phoneCallFromCollection !== undefined && initialLoadedPhoneCallFromCollection !== undefined) {
				const mergedPhoneCall = AggregateMerger.merge(
					initialLoadedPhoneCallFromCollection,
					phoneCallFromCollection,
					payload
				) as PhoneCall;
				this.phoneCalls.set(normalizeAggregateId(mergedPhoneCall.aggregateId), cloneDeep(mergedPhoneCall));
			} else {
				this.phoneCalls.set(normalizedAggregateId, payload);
			}

			this.initialLoadedPhoneCalls.set(normalizedAggregateId, cloneDeep(payload));
		},
		async setTheContactPictureForPhoneCallNumber(
			payload: SetTheContactPictureForPhoneCallNumberPayload
		): Promise<void> {
			const response = await payload.commandBus?.sendCommand(
				new SetTheContactPictureForThePhoneCallNumberCommand(
					payload.aggregateId,
					payload.phoneCallNumber,
					payload.contactPictureVersion,
					payload.payload
				)
			);

			if (response === null || response === undefined) {
				throw new Error('Contact picture command failed');
			}
		},
		setTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(
			payload: SetTheNextOutgoingCallIdForACalledNumberAfterAGivenTimestampPayload
		): void {
			this.nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp =
				payload.nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp;
		},
		setTheOutgoingCallConfigurationForTheUser(payload: SetTheOutgoingCallConfigurationForTheUserPayload): void {
			this.outgoingCallConfigurationForTheUser = payload.outgoingCallConfigurationForTheUser;
		},
		setThePhoneBookEntries(payload: SetThePhoneBookEntriesPayload): void {
			this.phoneBookEntries = payload.phoneBookEntries;
		},
		setThePhoneCallStateToReady(payload: SetThePhoneCallStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setThePhoneCallStateToSeen(payload: SetThePhoneCallStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setThePhoneCallStateToUnseen(payload: SetThePhoneCallStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		setThePhoneticSimilarPhoneBookEntries(payload: SetThePhoneticSimilarPhoneBookEntriesPayload): void {
			this.phoneticSimilarPhoneBookEntries = payload.phoneticSimilarPhoneBookEntries;
		},
		async unsubscribeFetchPhoneCall(payload: UnsubscribeFetchPhoneCallPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new PhoneCallQuery(payload.aggregateId));
			}

			this.setPhoneCall(null);
		},
		updateTheNewCallerName(payload: UpdateTheNewCallerNamePayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.newCallerName = payload.phoneCallCallerName;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallCallBackDesiredState(payload: UpdateThePhoneCallCallBackDesiredStatePayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callbackDesired = payload.phoneCallBackDesiredState;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallCallDirection(payload: UpdateThePhoneCallCallDirectionPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.direction = payload.phoneCallDirection;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallCallerName(payload: UpdateThePhoneCallCallerNamePayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callerName = payload.phoneCallCallerName;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		async updateThePhoneCallPhoneBookEntryName(
			payload: UpdateThePhoneCallPhoneBookEntryNamePayload
		): Promise<void> {
			await payload.commandBus?.sendCommand(
				new UpdateThePhoneCallPhoneBookEntryName(payload.phonebookNumber2NameId, payload.updatedName)
			);
		},
		async updateThePhoneCallPhoneBookEntryNumber(
			payload: UpdateThePhoneCallPhoneBookEntryNumberPayload
		): Promise<void> {
			await payload.commandBus?.sendCommand(
				new UpdateThePhoneCallPhoneBookNameNumber(payload.phonebookNumber2NameId, payload.updatedNumber)
			);
		},
		updateThePhoneCallReason(payload: UpdateThePhoneCallReasonPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callReason = payload.phoneCallReason;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallReceivedBy(payload: UpdateThePhoneCallReceivedByPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callReceivedBy = payload.callReceivedBy;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallReceiver(payload: UpdateThePhoneCallReceiverPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callReceivedFor = payload.callReceivedFor;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateThePhoneCallSummary(payload: UpdateThePhoneCallSummaryPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callSummary = payload.phoneCallSummary;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateTheUpdatedCallerName(payload: UpdateTheUpdatedCallerNamePayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.updatedCallerName = payload.phoneCallCallerName;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
		updateTheUpdatedCallerNumber(payload: UpdateTheUpdatedCallerNumberPayload): void {
			const phoneCallFromCollection = this.phoneCalls.get(normalizeAggregateId(payload.aggregateId));

			if (phoneCallFromCollection !== undefined) {
				phoneCallFromCollection.callerNumber = payload.phoneCallCallerNumber;
				this.phoneCalls.set(normalizeAggregateId(phoneCallFromCollection.aggregateId), phoneCallFromCollection);
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
